/* eslint-disable  @typescript-eslint/no-explicit-any */
import { TAnswersState } from '../types/internal';
import { EAnswerField, TFormStoreData, EStepCode } from '../../../types/form';
import { getSkipStepsFromQueryParams } from './getSkipStepsFromQueryParams';

/**
 * Утверждает что переданный строковый ключ является валидным ключом для стейта ответов
 * */
function _assertKeyIsAnswersField(key: string): key is EAnswerField {
  return Object.values(EAnswerField).includes(key as EAnswerField);
}

/**
 * Парсит переданный набор параметров в валидный стейт ответов
 * */
const _parsePredefinedAnswers = <T extends { [key: string]: any }>(params: T): Partial<TAnswersState> => {
  return Object.keys(params).reduce((acc, key) => {
    if (_assertKeyIsAnswersField(key) && params[key]) {
      acc[key] = {
        answerField: key,
        value: params[key],
      };
    }

    return acc;
  }, {} as Partial<TAnswersState>);
};

/**
 * Парсит переданные множественные наборы параметров (источники данных) в валидный стейт ответов
 * */
const getPartialAnswersStateFromMultipleSources = (
  sources: Array<{ [key: string]: any } | null | undefined>,
): Partial<TAnswersState> => {
  const result: Partial<TAnswersState> = {};

  sources.forEach(source => {
    if (source) {
      Object.assign(result, _parsePredefinedAnswers(source));
    }
  });

  return result;
};

/**
 * Собирает переданные множественные наборы skipSteps (разные источники данных) в один набор skipSteps
 * */
const getSkipStepsFromMultipleSources = (sources: Array<EStepCode[] | null | undefined>): EStepCode[] => {
  const result: Set<EStepCode> = new Set();

  sources.forEach(source => {
    if (source) {
      source.forEach(result.add, result);
    }
  });

  return Array.from(result);
};

export interface IParseInitialAnswersStateParams {
  userData?: TFormStoreData['userData'];
  formDataFromQs?: { formData?: { [key: string]: any } };
}

/**
 * Парсит части стейта формы из переданных источников.
 * Данные для предустановки берутся из state.formData.userData (т.е из API) и из query params
 * */
export const parseInitialAnswersState = (params: IParseInitialAnswersStateParams): Partial<TAnswersState> => {
  const { userData, formDataFromQs } = params;

  const partialAnswersSources: Array<{ [key: string]: any } | null | undefined> = [userData, formDataFromQs?.formData];

  return getPartialAnswersStateFromMultipleSources(partialAnswersSources);
};

export interface IParseSkipStepsParams {
  skipSteps?: TFormStoreData['skipSteps'];
  formDataFromQs?: { skipSteps?: TFormStoreData['skipSteps']; formData?: Record<string, any> };
}

/** Парсит список пропускаемых шагов */
export const parseSkipSteps = (params: IParseSkipStepsParams): EStepCode[] => {
  const { skipSteps, formDataFromQs } = params;

  const skipStepsFromQs = getSkipStepsFromQueryParams(formDataFromQs);

  const skipStepsSources: Array<EStepCode[] | null | undefined> = [skipSteps, skipStepsFromQs];

  return getSkipStepsFromMultipleSources(skipStepsSources);
};
